import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Users2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { metricsApiClient } from '../../api/http/wireApiClient'
import type { WireArticle } from '../../api/types'
import type { ArticleActivityEntry, ArticleNavMethodsByArticleEntry } from '../metrics/types'
import { dateToApi, nDaysAgoStr, todayStr } from '../metrics/helpers'

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return { fg: '#111111', fgSecondary: '#6b7280', line: '#e5e7eb', tooltipBg: '#111111', tooltipFg: '#fcfcfc' }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg:          get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line:        get('--gray-6')  || '#e5e7eb',
    tooltipBg:   get('--gray-12') || '#111111',
    tooltipFg:   get('--gray-1')  || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

interface ReaderImpactWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

type ReaderImpactRow = {
  articleId: string
  title: string
  views: number
  uniqueReaders: number
  avgTimeMs: number
}

function truncateTitle(title: string, max = 32): string {
  return title.length > max ? title.slice(0, max - 1) + '…' : title
}

function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  return `${Math.round(s / 3600)}h`
}

function useReaderImpact(articles: WireArticle[]) {
  const allArticleIds = useMemo(() => articles.map((article) => article.id), [articles])

  return useQuery({
    queryKey: ['admin', 'reader-impact', allArticleIds.join(',')],
    queryFn: async () => {
      if (allArticleIds.length === 0) return []

      const params = { from: dateToApi(nDaysAgoStr(90), false), to: dateToApi(todayStr(), true) }
      const { data: activityRows = [] } = await metricsApiClient.get<ArticleActivityEntry[]>('/metrics/article/activity', { params })
      const articleIdSet = new Set(allArticleIds)
      const relevantActivityRows = activityRows.filter((row) => articleIdSet.has(row.articleId))

      const byActivity = Array.from(
        relevantActivityRows.reduce((map, row) => {
          const current = map.get(row.articleId) ?? { articleId: row.articleId, totalTimeMs: 0, eventCount: 0 }
          current.totalTimeMs += row.timeSpentMs
          current.eventCount += 1
          map.set(row.articleId, current)
          return map
        }, new Map<string, { articleId: string; totalTimeMs: number; eventCount: number }>()),
      )
        .map(([, stats]) => stats)
        .sort((a, b) => b.totalTimeMs - a.totalTimeMs || b.eventCount - a.eventCount)
        .map((stats) => stats.articleId)

      const selectedArticleIds = Array.from(new Set([...byActivity, ...allArticleIds])).slice(0, 8)

      const navResults = await Promise.allSettled(
        selectedArticleIds.map(async (articleId) => {
          const { data } = await metricsApiClient.get<ArticleNavMethodsByArticleEntry>(`/metrics/nav-methods/${articleId}`, { params })
          return [articleId, data] as const
        }),
      )

      const navById = Object.fromEntries(
        navResults
          .filter((result): result is PromiseFulfilledResult<readonly [string, ArticleNavMethodsByArticleEntry]> => result.status === 'fulfilled')
          .map((result) => result.value),
      )

      return selectedArticleIds.map((articleId) => {
        const rows = relevantActivityRows.filter((row) => row.articleId === articleId)
        const totalTimeMs = rows.reduce((sum, row) => sum + row.timeSpentMs, 0)
        const uniqueReaders = new Set(rows.map((row) => row.clientDn)).size
        const avgTimeMs = rows.length > 0 ? Math.round(totalTimeMs / rows.length) : 0
        const views = navById[articleId]?.totalViews ?? 0

        return { articleId, views, uniqueReaders, avgTimeMs }
      })
    },
    enabled: allArticleIds.length > 0,
    staleTime: 60_000,
  })
}

export function ReaderImpactWidget({ articles, embedded = false }: ReaderImpactWidgetProps) {
  const { data = [], isPending } = useReaderImpact(articles)
  const colors = useChartTheme()

  const rows = useMemo<ReaderImpactRow[]>(() => {
    const titleById = new Map(articles.map((article) => [article.id, article.title]))
    return data.map((row) => ({
      ...row,
      title: titleById.get(row.articleId) ?? row.articleId,
    })).sort((a, b) => Math.max(b.views, b.uniqueReaders) - Math.max(a.views, a.uniqueReaders) || b.views - a.views)
  }, [articles, data])

  const totals = useMemo(() => ({
    views: rows.reduce((sum, row) => sum + row.views, 0),
    readers: rows.reduce((sum, row) => sum + row.uniqueReaders, 0),
  }), [rows])

  const chartOption = useMemo(() => {
    if (rows.length === 0) return null
    const labels = rows.map((row) => truncateTitle(row.title))
    const chartHeight = Math.max(180, rows.length * 40)
    return {
      _chartHeight: chartHeight,
      grid: { top: 8, right: 24, bottom: 44, left: 8, containLabel: true },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { seriesName: string; value: number }[]) => {
          const idx = (params[0] as { dataIndex?: number }).dataIndex ?? 0
          const row = rows[idx]
          if (!row) return ''
          return [
            `<b style="font-size:12px">${row.title}</b>`,
            `Views: ${row.views}`,
            `Unique readers: ${row.uniqueReaders}`,
            `Avg time: ${fmtDuration(row.avgTimeMs)}`,
          ].join('<br/>')
        },
      },
      legend: {
        bottom: 4,
        textStyle: { color: colors.fg, fontSize: 11, fontWeight: 600 },
      },
      xAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600 },
        splitLine: { lineStyle: { color: colors.line } },
      },
      yAxis: {
        type: 'category' as const,
        data: labels,
        inverse: true,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600, width: 140, overflow: 'truncate' as const },
        axisLine: { lineStyle: { color: colors.line } },
      },
      series: [
        {
          name: 'Views',
          type: 'bar' as const,
          data: rows.map((row) => row.views),
          barMaxWidth: 16,
          itemStyle: { color: '#2563eb', borderRadius: [0, 4, 4, 0] },
        },
        {
          name: 'Readers',
          type: 'bar' as const,
          data: rows.map((row) => row.uniqueReaders),
          barMaxWidth: 16,
          itemStyle: { color: '#7c3aed', borderRadius: [0, 4, 4, 0] },
        },
      ],
    }
  }, [rows, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {isPending ? (
        <div className="space-y-2 rounded-xl bg-canvas px-4 py-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-fill" />)}
        </div>
      ) : chartOption ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts
            option={chartOption}
            style={{ height: (chartOption as { _chartHeight: number })._chartHeight }}
          />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No readership data yet for the current articles.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {totals.views > 0
          ? `${totals.views} total views across ${rows.filter((r) => r.views > 0).length} article${rows.filter((r) => r.views > 0).length !== 1 ? 's' : ''}, ${totals.readers} unique readers.`
          : 'No readership data in the last 90 days for these drafts.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Users2 size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Reader Impact</span>
      </div>
      {body}
    </section>
  )
}
